import {
  AgentIcon,
  BookIcon,
  DatabaseIcon,
  IdBadgeIcon,
  ListUnorderedIcon,
  PlugIcon,
  ServerIcon,
} from '@primer/octicons-react'
import {advancedSearchPath, getRelativeHref, searchPath} from '@github-ui/paths'

import type {SearchKindCount} from '../../hooks/use-search-result-counts'
import type {SearchResults} from '../../types/blackbird-types'
import {AIResourceSearchPage, type AIResourceSearchPageState} from './AIResourceSearchPage'
import type {ResourceTypeFilterProps} from './ResourceTypeFilter'
import type {AIResourceSearchCategory} from './SearchCategoryNav'

type AIResourceSearchProps = {
  counts: readonly SearchKindCount[]
  payload: SearchResults | null
  query: string
}

const resourceTypes = [
  {label: 'Skill', qualifier: 'type:"Skill"', icon: BookIcon},
  {label: 'MCP Server', qualifier: 'type:"MCP Server"', icon: ServerIcon},
  {label: 'Agent', qualifier: 'type:"Agent"', icon: AgentIcon},
  {label: 'Plugin', qualifier: 'type:"Plugin"', icon: PlugIcon},
  {label: 'AI Catalog', qualifier: 'type:"AI Catalog"', icon: ListUnorderedIcon},
  {label: 'AI Registry', qualifier: 'type:"AI Registry"', icon: DatabaseIcon},
  {label: 'A2A Card', qualifier: 'type:"A2A Card"', icon: IdBadgeIcon},
] as const

const clearedSearchParameters = {p: null, l: null, s: null, o: null}

export function AIResourceSearch({counts, payload, query}: AIResourceSearchProps) {
  const categories: AIResourceSearchCategory[] = counts.map(count => ({
    label: capitalize(count.readableNamePlural),
    href: getRelativeHref(searchPath, undefined, {...clearedSearchParameters, q: query, type: count.name}),
    icon: count.icon,
    count: count.final && !count.error ? count.total : undefined,
    current: count.name === 'ai_resources',
  }))

  const filters: ResourceTypeFilterProps = {
    resourceTypes: resourceTypes.map(resourceType => ({
      label: resourceType.label,
      href: getFilterHref(query, resourceType.qualifier),
      icon: resourceType.icon,
    })),
    labelFilterHref: getFilterHref(query, 'label:'),
    advancedSearchHref: getRelativeHref(advancedSearchPath, undefined, {q: query}),
  }

  return <AIResourceSearchPage categories={categories} filters={filters} query={query} state={getState(payload)} />
}

function getFilterHref(query: string, qualifier: string) {
  return getRelativeHref(searchPath, undefined, {
    ...clearedSearchParameters,
    q: `${query} ${qualifier}`.trim(),
    type: 'ai_resources',
  })
}

function getState(payload: SearchResults | null): AIResourceSearchPageState {
  if (!payload) return {status: 'loading'}
  if (payload.errors.length > 0) return {status: 'error'}
  return {status: 'empty'}
}

function capitalize(value: string) {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}
